import { useCallback, useRef, useState } from 'react';
import {
  Divider,
  Highlight,
  IconStatusCorrect,
  Stack,
  Text,
  useToast,
} from '@adc/parallax-component-library';

import { useDepartmentDetails } from '~/api/query/departmentDetails';
import { useDepartmentSettingsUpdateMutation } from '~/api/query/departmentSettings';
import {
  DepartmentAlertForm,
  type DepartmentAlertFormRef,
  type DepartmentAlertFormValues,
} from '~/components/forms/department/DeparmentAlertForm';
import { Modal } from '~/components/Modal';
import { useModal } from '~/features/modals/useModal';
import { buildTestId } from '~/utils/buildTestId';

type DepartmentDetailsAlertModalProps = {
  testID: string;
  departmentId: string;
};

export const DepartmentAlertFormModal = ({
  testID,
  departmentId,
}: DepartmentDetailsAlertModalProps) => {
  const [hasFormChanged, setHasFormChanged] = useState(false);

  const { showToast } = useToast();
  const { closeModal } = useModal();

  const formRef = useRef<DepartmentAlertFormRef>(null);

  const {
    data: department,
    isLoading,
    error,
  } = useDepartmentDetails(departmentId);

  const { mutateAsync: updateDepartmentSettings } =
    useDepartmentSettingsUpdateMutation();

  const onFormSubmit = useCallback(
    async (values: DepartmentAlertFormValues) => {
      await updateDepartmentSettings({
        id: departmentId,
        alertsSettings: {
          alertPhone: values.alertPhone,
        },
      });

      closeModal('DepartmentAlertForm');

      showToast({
        message: 'Department settings updated',
        'aria-label': 'Update successful',
        icon: <IconStatusCorrect accentColor="$support.positive.default" />,
      });
    },
    [closeModal, showToast, departmentId, updateDepartmentSettings],
  );

  return (
    <Modal
      testID={testID}
      title={isLoading ? 'Loading' : 'Edit patient alerts'}
      hasCloseButton
      size="m"
      primaryAction={{
        label: 'Save Changes',
        disabled: !hasFormChanged,
        onPress: () => {
          void formRef.current?.submitForm();
        },
      }}
      secondaryAction={{
        label: 'Cancel',
        onPress: () => {
          closeModal('DepartmentAlertForm');
        },
      }}
      onOpenChange={() => {
        closeModal('DepartmentAlertForm');
      }}
    >
      {!error && department && (
        <Stack>
          <Text marginBottom="$4">
            All patients in {department.name} receive this alert when their
            alert conditions are met and their alerts are enabled.
          </Text>
          <Text type="$bodyBase.strong" marginVertical="$2">
            Alert message
          </Text>
          <Divider marginVertical="$1" />
          <DepartmentAlertForm
            ref={formRef}
            testID={buildTestId({
              testID,
              suffix: 'form',
            })}
            hasFormChanged={setHasFormChanged}
            onSubmit={onFormSubmit}
            defaultValues={{
              alertPhone: department.alertsSettings.alertPhone,
            }}
          />
        </Stack>
      )}
      {error && (
        <Highlight
          description="There was an error loading the department data."
          state="danger"
          testID={buildTestId({
            testID,
            suffix: ['error', 'department', 'not', 'found'],
          })}
          title="Department data not found"
        />
      )}
    </Modal>
  );
};
